import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withStyles } from '@material-ui/styles';
import { isEmpty, isNil } from 'lodash';
import { calculateOptimalEmailSize, formatBytes } from 'utils/misc';
import Drawer from '../';
import MetricBar from './MetricBar';

const styles = theme => ({
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '0.75rem',
    color: theme.palette.secondary['900'],
  },
  checkStatus: {
    fontSize: '0.875rem',
    color: theme.palette.accent2['1000'],
    textTransform: 'uppercase',
    fontWeight: 'bold',
  },
  metricsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
  },
  divider: {
    height: '1px',
    width: '100%',
    marginTop: 0,
    marginBottom: 0,
  },
});


const PitchQualityCheck = ({ classes, data }) => {
  const {
    subject_length: subjectLength,
    body_word_count: bodyWordCount,
    question_count: questionCount,
    spam_word_count: spamWordCount,
    spam_words: spamWords,
    personalization,
    email_size: emailSize,
  } = data || {};


  if (isEmpty(data)) {
    return null;
  }
  const [emailSizeValue, emailSizeUnit] = isNil(emailSize) ? [null, null] : formatBytes(emailSize).split(' ');
  const optimalSize = calculateOptimalEmailSize(emailSizeUnit);
  return (
    <Drawer>
      {/* <div className={classes.titleWrapper}>
        <span className={classes.title}>
        Chance of response
        </span>
        <span className={classes.checkStatus}>Moderately</span>
      </div> */}
      <div className={classes.metricsWrapper}>
        <MetricBar
          label="Spam word count"
          value={spamWordCount}
          tooltip={!isEmpty(spamWords)
            ? <div>Optimal: 0<br />Spam words: {spamWords.join(', ')}</div>
            : 'Optimal: 0'}
          info={'The Spam word count metric shows how many words, recognized by spam \
          filters as spam words, are used in the text. To improve your reply rate, \
          try not to use them in your emails.'}
        >
          <MetricBar.InnerBar
            weight={1}
            min={0}
            max={0}
            color={MetricBar.colors.good}
          />
          <MetricBar.InnerBar
            weight={1}
            min={1}
            max={1}
            color={MetricBar.colors.neutral}
          />
          <MetricBar.InnerBar
            weight={1}
            min={2}
            color={MetricBar.colors.bad}
          />
        </MetricBar>
        <MetricBar
          label="Email size"
          value={emailSizeValue}
          unit={emailSizeUnit}
          info="Even if pitch has multiple images, they should be condensed into one file."
          tooltip="Optimal: 1 MB"
        >
          <MetricBar.InnerBar
            weight={1}
            min={0}
            max={optimalSize}
            color={MetricBar.colors.good}
          />
          <MetricBar.InnerBar
            weight={1}
            min={optimalSize}
            color={MetricBar.colors.bad}
          />
        </MetricBar>
        <hr className={classes.divider} />
        <MetricBar
          label="Personalization"
          value={personalization}
          unit="variables"
          tooltip="Optimal: > 2 variables"
          info={
            // eslint-disable-next-line max-len
            <>Personalization of pitches is the key to getting responses,
              but also be sure you are targeting the appopriate audience.{' '}
              Use variables in your pitches as well as the customize by contact feature.<br />
              More details{' '}
              <a
                href="https://help.prvolt.com/en/articles/5245790-pitch-writing-best-practices"
                target="_blank"
                rel="noopener noreferrer"
              >here
              </a>.
            </>}
        >
          <MetricBar.InnerBar
            weight={1}
            min={0}
            max={1}
            color={MetricBar.colors.bad}
          />
          <MetricBar.InnerBar
            weight={1}
            min={2}
            max={2}
            color={MetricBar.colors.neutral}
          />
          <MetricBar.InnerBar
            weight={1}
            min={3}
            color={MetricBar.colors.good}
          />
        </MetricBar>
        {!isNil(subjectLength) && (
          <MetricBar
            label="Subject length"
            value={subjectLength}
            unit="characters"
            tooltip="Optimal: 50-60"
            info={
              <>
                Best practice is to consider the length of your subject line and how it will
                appear on mobile devices.<br />
                More details{' '}
                <a
                  href="https://help.prvolt.com/en/articles/5253571-key-tips-for-writing-great-subject-lines"
                  target="_blank"
                  rel="noopener noreferrer"
                >here
                </a>.
              </>
            }
          >
            <MetricBar.InnerBar
              weight={1}
              min={0}
              max={5}
              color={MetricBar.colors.bad}
            />
            <MetricBar.InnerBar
              weight={3}
              min={6}
              max={60}
              color={MetricBar.colors.good}
            />
            <MetricBar.InnerBar
              weight={1}
              min={61}
              max={70}
              color={MetricBar.colors.neutral}
            />
            <MetricBar.InnerBar
              weight={2}
              min={71}
              color={MetricBar.colors.bad}
            />
          </MetricBar>
        )}
        <MetricBar
          label="Body word count"
          value={bodyWordCount}
          unit="words"
          info="Word count above the signature."
          tooltip="Optimal: 200 - 300 words"
        >
          <MetricBar.InnerBar
            weight={1}
            min={0}
            max={99}
            color={MetricBar.colors.bad}
          />
          <MetricBar.InnerBar
            weight={1}
            min={100}
            max={199}
            color={MetricBar.colors.neutral}
          />
          <MetricBar.InnerBar
            weight={1}
            min={200}
            max={300}
            color={MetricBar.colors.good}
          />
          <MetricBar.InnerBar
            weight={4}
            min={301}
            max={900}
            color={MetricBar.colors.neutral}
          />
          <MetricBar.InnerBar
            weight={2}
            min={901}
            color={MetricBar.colors.bad}
          />
        </MetricBar>
        <MetricBar
          label="Question count"
          value={questionCount}
          tooltip="Optimal: 1"
          info={
            <>
              Asking a question in your email gives you the best chance of getting a response.
              See{' '}
              <a
                href="https://help.prvolt.com/en/articles/5245790-pitch-writing-best-practices"
                target="_blank"
                rel="noopener noreferrer"
              >this article
              </a> for more details.
            </>
          }
        >
          <MetricBar.InnerBar
            weight={1}
            min={0}
            max={0}
            color={MetricBar.colors.bad}
          />
          <MetricBar.InnerBar
            weight={1}
            min={1}
            max={1}
            color={MetricBar.colors.good}
          />
          <MetricBar.InnerBar
            weight={1}
            min={2}
            max={2}
            color={MetricBar.colors.neutral}
          />
          <MetricBar.InnerBar
            weight={1}
            min={3}
            color={MetricBar.colors.bad}
          />
        </MetricBar>
      </div>
    </Drawer>
  );
};

PitchQualityCheck.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  data: PropTypes.shape({
    spam_words: PropTypes.arrayOf(PropTypes.string).isRequired,
    subject_length: PropTypes.number.isRequired,
    question_count: PropTypes.number.isRequired,
    spam_word_count: PropTypes.number.isRequired,
    body_word_count: PropTypes.number.isRequired,
    personalization: PropTypes.number.isRequired,
  }).isRequired,
};

export default compose(
  withStyles(styles),
  connect(state => ({ data: state.pitchQualityMetrics })),
)(PitchQualityCheck);
